import { Download, File, Folder, FolderOpen, Globe } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { KanzleilandIntegration } from "@/apis/gql/types";
import TreeView, {
  type TreeViewItem,
} from "@/components/shared/tree-view-select/tree-view";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyP, TypographySmall } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

import {
  type FoldersObject,
  transformFoldersToHierarchy,
} from "../folder-transformer";

const customIconMap = {
  region: <Globe className="h-4 w-4 text-purple-500" />,
  store: <Folder className="h-4 w-4 text-blue-500" />,
  department: <FolderOpen className="h-4 w-4 text-green-500" />,
  item: <File className="h-4 w-4 text-orange-500" />,
};

const menuItems = [
  {
    id: "download",
    label: "Download",
    icon: <Download className="h-4 w-4" />,
    action: (items: any) => console.log("Downloading:", items),
  },
];

type Props = {
  disabled: boolean;
  folders?: FoldersObject;
  selectedFolders?: KanzleilandIntegration["integrationConfig"]["folderIds"];
  onSelectionChange?: (
    selectedFolders: KanzleilandIntegration["integrationConfig"]["folderIds"],
  ) => Promise<void>;
};

export const KanzleilandIntegrationStepThree = ({
  disabled,
  folders,
  selectedFolders: _selectedFolders,
  onSelectionChange,
}: Props) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<
    KanzleilandIntegration["integrationConfig"]["folderIds"]
  >(_selectedFolders ?? []);

  const handleSelectionChange = async () => {
    setLoading(true);
    try {
      await onSelectionChange?.(selectedFolders);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", disabled && "opacity-50")}>
      <div className="flex flex-row gap-2 items-center">
        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
          <TypographyP className="text-muted-foreground">3</TypographyP>
        </div>
        <div className="flex flex-col gap-1">
          <TypographySmall>
            {t("integrations.kanzleiland.step_3")}
          </TypographySmall>
          <TypographySmall className="text-muted-foreground">
            {t("integrations.kanzleiland.step_3_description")}
          </TypographySmall>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex justify-center h-auto w-10" />
        <div className="flex flex-1 mt-2 flex-col gap-4">
          <div className="flex-1">
            {!disabled && folders && (
              <FoldersView
                folders={folders}
                selectedFolders={selectedFolders}
                onSelectionChange={setSelectedFolders}
              />
            )}
          </div>
          {!disabled && (
            <div className="flex justify-start">
              <Button
                type="submit"
                className="gap-2"
                onClick={handleSelectionChange}
              >
                {loading && <LoadingSpinner />}
                {t("integrations.kanzleiland.save")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type FoldersViewProps = {
  folders: FoldersObject;
  selectedFolders?: KanzleilandIntegration["integrationConfig"]["folderIds"];
  onSelectionChange?: (
    selectedFolders: KanzleilandIntegration["integrationConfig"]["folderIds"],
  ) => void;
};

const FoldersView = ({
  folders,
  selectedFolders,
  onSelectionChange,
}: FoldersViewProps) => {
  const setCheckedItems = (
    items: TreeViewItem[],
    selectedIds: string[],
  ): TreeViewItem[] => {
    return items.map((item) => ({
      ...item,
      checked: selectedIds.includes(item.id),
      children: item.children
        ? setCheckedItems(item.children, selectedIds)
        : undefined,
    }));
  };

  const initialData = useMemo(() => {
    const treeData = transformFoldersToHierarchy(folders);

    if (selectedFolders) {
      const selectedIds = selectedFolders.map((folder) => folder.folderPath);
      return setCheckedItems(treeData, selectedIds);
    }

    return treeData;
  }, [folders, selectedFolders]);

  const [treeData, setTreeData] = useState<TreeViewItem[]>(initialData);

  const updateItemChecked = useCallback(
    (
      items: TreeViewItem[],
      targetId: string,
      checked: boolean,
    ): TreeViewItem[] => {
      return items.map((item) => {
        if (item.id === targetId) {
          const updatedItem = { ...item, checked };

          if (updatedItem.children) {
            updatedItem.children = updateAllChildren(
              updatedItem.children,
              checked,
            );
          }

          return updatedItem;
        } else if (item.children) {
          const updatedChildren = updateItemChecked(
            item.children,
            targetId,
            checked,
          );
          return { ...item, children: updatedChildren };
        }

        return item;
      });
    },
    [],
  );

  const updateAllChildren = (
    children: TreeViewItem[],
    checked: boolean,
  ): TreeViewItem[] => {
    return children.map((child) => ({
      ...child,
      checked,
      children: child.children
        ? updateAllChildren(child.children, checked)
        : undefined,
    }));
  };

  const handleCheckChange = useCallback(
    (item: TreeViewItem, checked: boolean) => {
      const newTreeData = updateItemChecked(treeData, item.id, checked);
      setTreeData(newTreeData);

      if (onSelectionChange) {
        const selectedItems = extractSelectedItems(newTreeData);
        const folderIds = selectedItems.map((item) => {
          const folderPath = item.id;
          const folderId = item.id.split(".").pop() || item.id;

          return {
            folderId,
            folderPath,
            type: item.id.startsWith("kanzlei")
              ? ("kanzlei" as const)
              : ("mandant" as const),
          };
        });
        onSelectionChange(folderIds);
      }
    },
    [treeData, updateItemChecked, onSelectionChange],
  );

  const extractSelectedItems = (items: TreeViewItem[]): TreeViewItem[] => {
    const selected: TreeViewItem[] = [];
    items.forEach((item) => {
      if (item.checked && item.type === "department") {
        selected.push(item);
      }
      if (item.children) {
        selected.push(...extractSelectedItems(item.children));
      }
    });
    return selected;
  };

  return (
    <TreeView
      data={treeData}
      showCheckboxes={true}
      iconMap={customIconMap}
      menuItems={menuItems}
      onCheckChange={handleCheckChange}
    />
  );
};
